import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, inr } from "@/lib/api";
import { LOCATIONS } from "@/data/content";
import { Reveal, SectionLabel } from "./Reveal";

const GameCard = ({ amount, bonus }) => (
  <motion.div whileHover={{ rotate: -2, scale: 1.02 }} transition={{ type: "spring", stiffness: 200 }}
    className="relative aspect-[1.6/1] w-full max-w-[460px] rounded-[26px] bg-[#FFEF00] border-[3px] border-black shadow-[10px_10px_0_#14C3F7] p-6 md:p-8 overflow-hidden text-black" data-testid="recharge-game-card">
    <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-[#14C3F7] border-[3px] border-black" />
    <div className="absolute right-10 -bottom-16 w-40 h-40 rounded-full border-[14px] border-black/10" />
    <img src="/brand/logo.png" alt="ZoopUp" className="relative h-9 md:h-11 w-auto" />
    <p className="relative mt-6 md:mt-10 font-mono text-[11px] uppercase tracking-[0.25em]">Game card balance</p>
    <p className="relative font-display font-black text-4xl md:text-5xl">{inr(amount + bonus)}</p>
    {bonus > 0 && <p className="relative mt-1 font-semibold text-sm">Includes {inr(bonus)} bonus</p>}
  </motion.div>
);

export const Recharge = ({ options }) => {
  const nav = useNavigate();
  const [amt, setAmt] = useState(1000);
  const [f, setF] = useState({ card_number: "", location: LOCATIONS[0].name, name: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const bonus = options.find((o) => o.amount === amt)?.bonus ?? 0;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", { kind: "recharge", recharge_amount: amt, guests: 1, ...f });
      nav(`/booking/success?code=${data.code}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <section id="recharge" data-testid="recharge-section" className="relative py-24 md:py-32 bg-[#0E101D] overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <SectionLabel color="text-[#FFEF00]">Game card</SectionLabel>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">Top up.<br /><span className="text-[#14C3F7]">Power up.</span></h2>
          <p className="mt-6 text-slate-300 max-w-md">Recharge your ZoopUp game card ahead of time and unlock bonus credits on bigger top-ups.</p>
          <div className="mt-10"><GameCard amount={amt} bonus={bonus} /></div>
        </Reveal>
        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="recharge-form" className="rounded-[28px] bg-[#07080E] border border-white/10 p-6 md:p-9">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-3">Choose amount</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {options.map((o) => (
                <button type="button" key={o.amount} data-testid={`recharge-amount-${o.amount}`} onClick={() => setAmt(o.amount)}
                  className={`rounded-2xl py-4 border-2 transition-colors ${amt === o.amount ? "bg-[#FFEF00] text-black border-black" : "border-white/15 hover:border-[#FFEF00]"}`}>
                  <span className="block font-display font-extrabold">{inr(o.amount)}</span>
                  <span className={`block text-[11px] font-mono mt-1 ${amt === o.amount ? "text-black/70" : "text-[#14C3F7]"}`}>{o.bonus ? `+${inr(o.bonus)} bonus` : "no bonus"}</span>
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <input data-testid="recharge-card-input" className="field" placeholder="Card number (optional for new card)" value={f.card_number} onChange={set("card_number")} />
              <select data-testid="recharge-location-select" className="field" value={f.location} onChange={set("location")}>
                {LOCATIONS.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
              </select>
              <input data-testid="recharge-name-input" className="field" placeholder="Full name" value={f.name} onChange={set("name")} required minLength={2} />
              <input data-testid="recharge-phone-input" className="field" placeholder="Phone" value={f.phone} onChange={set("phone")} required minLength={7} />
            </div>
            <input data-testid="recharge-email-input" type="email" className="field mt-4" placeholder="Email" value={f.email} onChange={set("email")} required />
            <button data-testid="recharge-submit-btn" disabled={busy} className="btn-zoop w-full mt-8 rounded-full py-4 font-display font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <Zap size={18} />} Recharge {inr(amt)}
            </button>
            <p className="mt-3 text-center text-xs text-slate-400">Show your code at the counter to pay & activate. Online payment coming soon.</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
